/**
 * Seattle Metro Solo - Phase 9: 商品補充バリデーター
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_09_goods_growth_seattle.md
 */

import type { Ctx } from 'boardgame.io';
import type { SeattleGameState } from '../../types';
import { SEATTLE_DICE_GROUPS, SEATTLE_GRAY_CITIES, SEATTLE_RULES } from '../../../data/map/seattle_metro/config';

/**
 * 商品配置引数
 */
export interface PlaceGoodsSeattleArgs {
  /** 選択したキューブのインデックス */
  cubeIndex: number;
  /** 配置先のCity/Town ID */
  locationId: string;
}

/**
 * バリデーション結果
 */
export interface PlaceGoodsSeattleValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * ダイス目に対応するlocationリストを取得
 */
export function getLocationsByDiceValue(diceValue: number): string[] {
  const group = SEATTLE_DICE_GROUPS.find(g => g.diceValue === diceValue);
  return group?.locationIds || [];
}

/**
 * 指定locationの現在の商品数を取得
 */
function getGoodsCountAtLocation(G: SeattleGameState, locationId: string): number {
  const hex = G.map.find(h => h.city?.id === locationId || h.town?.id === locationId);
  return hex?.goods?.length || 0;
}

/**
 * Gray City かどうかを判定
 */
function isGrayCity(locationId: string): boolean {
  return locationId === SEATTLE_GRAY_CITIES.DOWNTOWN || locationId === SEATTLE_GRAY_CITIES.AIRPORT;
}

/**
 * ロケーションに対応するダイス目を取得
 */
export function getDiceValueForLocation(locationId: string): number | null {
  for (const group of SEATTLE_DICE_GROUPS) {
    if (group.locationIds.includes(locationId)) {
      return group.diceValue;
    }
  }
  return null;
}

/**
 * Seattle 商品配置のバリデーション
 *
 * @param G - Seattle ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - 商品配置引数
 * @returns バリデーション結果
 */
export function validatePlaceGoodsSeattle(
  G: SeattleGameState,
  _ctx: Ctx,
  args: PlaceGoodsSeattleArgs
): PlaceGoodsSeattleValidationResult {
  const { cubeIndex, locationId } = args;

  // 0. pendingGoodsPlacement 存在チェック
  if (!G.pendingGoodsPlacement) {
    return {
      success: false,
      error: 'NO_PENDING_PLACEMENT',
      message: 'No pending goods placement data'
    };
  }

  const { remainingDice, remainingCubes } = G.pendingGoodsPlacement;

  // 1. cubeIndex 範囲チェック
  if (cubeIndex < 0 || cubeIndex >= remainingCubes.length) {
    return {
      success: false,
      error: 'INVALID_CUBE_INDEX',
      message: `Cube index ${cubeIndex} is out of range (0-${remainingCubes.length - 1})`
    };
  }

  // 2. Gray City チェック（出目5,6でもDowntown/Airportには配置不可）
  if (isGrayCity(locationId)) {
    return {
      success: false,
      error: 'GRAY_CITY_NOT_ALLOWED',
      message: 'Cannot place goods on Downtown/Airport during goods growth'
    };
  }

  // 3. location存在チェック
  const hex = G.map.find(h => h.city?.id === locationId || h.town?.id === locationId);
  if (!hex) {
    return {
      success: false,
      error: 'LOCATION_NOT_FOUND',
      message: `Location ${locationId} not found on map`
    };
  }

  // 4. ロケーションに対応するダイス目を取得
  const requiredDiceValue = getDiceValueForLocation(locationId);
  if (requiredDiceValue === null) {
    return {
      success: false,
      error: 'INVALID_LOCATION',
      message: `${locationId} does not belong to any dice group`
    };
  }

  // 5. 残りダイス目に対応するロケーションかチェック
  if (!remainingDice.includes(requiredDiceValue)) {
    return {
      success: false,
      error: 'NO_MATCHING_DICE',
      message: `No remaining dice with value ${requiredDiceValue} for location ${locationId}. Remaining dice: ${remainingDice.join(', ')}`
    };
  }

  // 6. 需要過多チェック
  const currentGoods = getGoodsCountAtLocation(G, locationId);
  if (currentGoods >= SEATTLE_RULES.maxGoodsPerLocation) {
    return {
      success: false,
      error: 'DEMAND_OVERLOAD',
      message: `${locationId} already has ${currentGoods} goods (max: ${SEATTLE_RULES.maxGoodsPerLocation})`
    };
  }

  return { success: true };
}

/**
 * 指定ダイス目で配置可能なlocationがあるかチェック
 *
 * @param G - Seattle ゲーム状態
 * @param diceValue - ダイス目
 * @returns 配置可能なlocationがあればtrue
 */
export function hasAvailableLocation(G: SeattleGameState, diceValue: number): boolean {
  const validLocations = getLocationsByDiceValue(diceValue);

  for (const locationId of validLocations) {
    const currentGoods = getGoodsCountAtLocation(G, locationId);
    if (currentGoods < SEATTLE_RULES.maxGoodsPerLocation) {
      return true;
    }
  }

  return false;
}

/**
 * 指定ダイス目で配置可能なlocationリストを取得
 */
export function getAvailableLocations(G: SeattleGameState, diceValue: number): string[] {
  const validLocations = getLocationsByDiceValue(diceValue);

  return validLocations.filter(locationId => {
    const currentGoods = getGoodsCountAtLocation(G, locationId);
    return currentGoods < SEATTLE_RULES.maxGoodsPerLocation;
  });
}
